// The Owner's Manual — plain-English product & upgrade education. Written to
// answer the questions a salesperson usually answers with an upsell. Every
// guide ends with the honest DIY line where relevant.

export interface GuideSection {
    h?: string
    p?: string
    list?: string[]
}

export interface Guide {
    id: string
    title: string
    kicker: string
    teaser: string
    sections: GuideSection[]
    stopDiy?: string
}

export const GUIDES: Guide[] = [
    {
        id: 'uv-lights',
        title: 'UV lights & air purifiers (including the REME HALO)',
        kicker: 'Air quality',
        teaser: 'What in-duct purifiers actually do, who benefits, and what’s marketing fluff.',
        sections: [
            {
                p: 'There are two different products that get lumped together as “UV lights,” and they do different jobs.',
            },
            {
                h: 'Coil-mounted germicidal UV',
                p: 'A UV-C bulb aimed at your evaporator coil. Its real job is housekeeping: the coil is cold, wet, and dark — perfect for mold and biofilm — and a UV lamp keeps it clean. A clean coil transfers heat better and smells better. This is the unglamorous option with the most consistent payoff, especially in humid climates.',
            },
            {
                h: 'In-duct air treatment (REME HALO and similar)',
                p: 'Units like the REME HALO go beyond the coil: they generate ionized hydrogen peroxide plasma that circulates with your air, targeting odors, VOCs, and airborne particles throughout the house. Owners with allergies, pets, or lingering-odor problems tend to notice the difference most. Two honest caveats: independent real-world (versus lab) data is thinner than the brochures suggest, and every cell/bulb is a consumable — budget for replacement every 2–3 years, typically $100–$300.',
            },
            {
                h: 'The straight answer',
                list: [
                    'Humid climate or a history of musty smells → coil UV is cheap insurance.',
                    'Allergies, pets, odors → an in-duct purifier is worth considering; ask for the cell replacement price up front.',
                    'Neither problem → skip both and put the money toward a better filter cabinet.',
                ],
            },
        ],
        stopDiy:
            'Installation involves cutting into the plenum and wiring into the air handler — installer territory. Bulb swaps on an existing unit, though, are usually a homeowner job: power off, gloves on (skin oils shorten bulb life), never look at a lit UV-C bulb.',
    },
    {
        id: 'addons',
        title: 'Add-ons worth knowing (and which to skip)',
        kicker: 'Upgrades',
        teaser: 'A frank usually-worth-it / situational / skip-it verdict on the common quote-sheet extras.',
        sections: [
            {
                h: 'Usually worth it',
                list: [
                    'Condensate float switch (~$100–$150): shuts the system off before an overflowing drain pan soaks your ceiling. The cheapest water-damage insurance that exists.',
                    'Surge protector (~$150–$300): modern systems have circuit boards and inverters; one nearby lightning strike costs more than a decade of protection.',
                    'Media filter cabinet (~$300–$600): a deep 4–5″ filter beats any 1″ filter on both air quality and airflow, and you change it once or twice a year instead of monthly.',
                ],
            },
            {
                h: 'Situational',
                list: [
                    'Compressor soft-start kit: earns its keep on generator/solar homes and units that dim the lights at startup; otherwise optional.',
                    'Smart thermostat: real savings if you’ll actually use schedules; a $250 thermostat set to “hold 72 forever” saves nothing.',
                    'Whole-home dehumidifier: transformative in genuinely humid climates when the AC alone can’t hold 50% RH; unnecessary elsewhere. (See the humidity guide.)',
                ],
            },
            {
                h: 'Usually skip',
                list: [
                    '“Maintenance kits” of sprays and pan tablets sold at markup — a $10 supply-store equivalent exists.',
                    'Duct-mounted “air scrubber” add-ons stacked on top of an already-purchased purifier — pick one air-quality strategy, not three.',
                ],
            },
        ],
    },
    {
        id: 'tiers',
        title: 'Equipment tiers, translated',
        kicker: 'Buying',
        teaser: 'Single-stage vs two-stage vs variable-speed — what actually changes, in comfort and in dollars.',
        sections: [
            {
                p: 'Every brand sells the same three tiers with different badges. The tier is about the compressor, and the compressor is about how gracefully the system can throttle.',
            },
            {
                h: 'Single-stage',
                p: 'On or off, nothing in between. Cheapest to buy, cheapest to fix, and perfectly adequate in mild climates or rentals. The costs: bigger temperature swings, more startup noise, and mediocre humidity removal because it rarely runs long enough to wring the air out.',
            },
            {
                h: 'Two-stage',
                p: 'Adds a “low gear” (roughly 65–70% capacity) the system uses most of the time, dropping to full blast only on the hottest days. Longer, quieter cycles mean steadier temperatures and noticeably better dehumidification. For most homes this is the sweet spot: most of the comfort gain for a fraction of the top-tier premium.',
            },
            {
                h: 'Variable-speed (inverter)',
                p: 'Throttles anywhere from ~25% to 100%, like cruise control. Best comfort, best humidity control, quietest, most efficient — and the most expensive to buy and to repair, because the electronics are sophisticated and brand-specific. Worth it for humid climates, allergy households, and anyone planning to stay 10+ years; overkill for a 5-year house.',
            },
        ],
    },
    {
        id: 'refrigerants',
        title: 'Refrigerants: why they keep changing',
        kicker: 'Regulations',
        teaser: 'R-22 → R-410A → R-32/R-454B, what each transition means for your repair bill.',
        sections: [
            {
                p: 'Three refrigerant eras, two forced transitions, and your repair costs depend heavily on which era your system belongs to.',
            },
            {
                h: 'R-22 (systems installed before ~2010)',
                p: 'Phased out for damaging the ozone layer; production and import were banned in 2020. Every recharge since draws on a shrinking pool of recycled supply, so a repair that needs refrigerant can run $150–$300+ per pound — often $1,000+ for a real leak. An R-22 system needing refrigerant work is usually a replace-the-system conversation, not a repair one.',
            },
            {
                h: 'R-410A (roughly 2010–2024 installs)',
                p: 'The ozone-safe replacement — which turned out to be a potent greenhouse gas (~2,088× CO₂). Under the AIM Act its production is stepping down through the 2020s. Nothing is banned about running your R-410A system, and it remains repairable, but expect recharge prices to climb the same way R-22’s did, on a slower curve.',
            },
            {
                h: 'R-32 and R-454B (new systems since 2025)',
                p: 'The current generation: far lower global-warming potential, slightly flammable (“A2L” class — safe in listed equipment, but service requires updated tooling and training). If you buy a new system today, it uses one of these, and that’s a point in favor of replacing at the natural end of an old system’s life rather than pouring money into a legacy refrigerant.',
            },
        ],
        stopDiy:
            'All refrigerant work — charging, recovering, even topping off — legally requires EPA Section 608 certification. There is no DIY lane here.',
    },
    {
        id: 'seer',
        title: 'SEER & SEER2, in dollars',
        kicker: 'Efficiency',
        teaser: 'What the sticker number really measures and what upgrading actually saves.',
        sections: [
            {
                p: 'SEER is cooling delivered per electricity consumed over a simulated season — a higher number means a cheaper summer. In 2023 the test got tougher (real-world duct pressures), and the rating was renamed SEER2. The numbers don’t line up exactly: 14.3 SEER2 ≈ 15 SEER. Current federal minimums for new central ACs are 13.4 SEER2 (northern states) and 14.3 SEER2 (southern states).',
            },
            {
                h: 'A worked example',
                p: 'Take a 3-ton system cooling 1,800 hours a year at $0.16/kWh. At 10 SEER (a typical 2005-era unit) that’s roughly $1,040 a summer. The same load at 15 SEER2 costs about $650; at 17+ SEER2, under $580. Call it $350–$450 a year back for a mid-tier modern unit — real money, but rarely enough to justify replacing a healthy system on efficiency alone.',
            },
            {
                h: 'The honest framing',
                list: [
                    'Efficiency savings are a strong tiebreaker when the system is already old or broken.',
                    'They are a weak reason to retire a working 8-year-old unit — the payback math almost never closes.',
                    'Past ~16 SEER2, each extra point buys less; spend the difference on correct sizing and sealed ducts instead (see the sizing guide).',
                ],
            },
        ],
    },
    {
        id: 'sizing',
        title: 'Sizing: why bigger isn’t better',
        kicker: 'Buying',
        teaser: 'Tons, BTUs, Manual J — and why an oversized AC makes your house clammy.',
        sections: [
            {
                p: 'AC capacity is measured in tons (1 ton = 12,000 BTU/hour). The trap is assuming more is safer. An air conditioner does two jobs: cooling the air and wringing moisture out of it — and the second job takes time. An oversized unit blasts the thermostat down in ten minutes and shuts off before it dehumidifies, a pattern called short cycling. The result is a house that is cold, damp, and clammy, plus extra wear from constant starts. Undersized has the opposite failure: it simply never catches up on the hottest days.',
            },
            {
                h: 'What a real sizing job looks like',
                p: 'The industry-standard method is a Manual J load calculation: square footage plus insulation levels, window area and orientation, ceiling heights, air-tightness, local climate, even shade trees. Two same-size houses can need different equipment. Square-footage rules of thumb (“500 sq ft per ton”) ignore all of that, and contractors who skip the math usually round up to protect themselves — which is exactly the failure mode above.',
            },
            {
                h: 'What to demand from any installer quote',
                list: [
                    'A Manual J calculation for YOUR house — not the old unit’s size copied forward (it may have been wrong for 20 years).',
                    'The report itself; a contractor who did the math will happily show it.',
                    'Suspicion of anyone who quotes a tonnage over the phone.',
                ],
            },
        ],
    },
    {
        id: 'humidity',
        title: 'Humidity: the comfort setting nobody talks about',
        kicker: 'Comfort & health',
        teaser: 'The 30–50% target, why it matters more than temperature, and how AC fits in.',
        sections: [
            {
                p: 'Half of “my AC isn’t keeping up” complaints are really humidity complaints. Air at 76°F and 45% humidity feels better than air at 72°F and 65% — dry air lets your body’s own cooling work.',
            },
            {
                h: 'The target',
                p: 'Keep indoor relative humidity between 30% and 50%, with ~45% as the sweet spot. Above 60%, mold and dust mites thrive and the house feels sticky no matter the temperature. Below 30% (a winter problem), you get static shocks, cracked wood, and irritated sinuses. A $15 hygrometer tells you where you stand.',
            },
            {
                h: 'How the AC fits in',
                p: 'Cooling air squeezes water out of it — a healthy, correctly-sized AC is your primary dehumidifier, pulling gallons per day out of the house in summer. This is why oversizing ruins comfort (see the sizing guide): a unit that short-cycles never runs long enough to do this job. If the AC is right-sized and the house still can’t hold 50% in a swampy climate, that’s the legitimate use case for a whole-home dehumidifier — or, at replacement time, a two-stage or variable-speed system that runs longer, gentler cycles.',
            },
        ],
    },
    {
        id: 'filters',
        title: 'Filters 101',
        kicker: 'Maintenance',
        teaser: 'MERV ratings, change intervals, and the one habit that prevents most breakdowns.',
        sections: [
            {
                p: 'The air filter is the single highest-leverage maintenance item on your system. A loaded filter starves airflow — the root cause behind weak cooling, frozen coils, and early blower death.',
            },
            {
                h: 'MERV, decoded',
                p: 'MERV rates how fine a particle the filter catches (higher = finer). MERV 8 handles dust and pollen; MERV 11–13 adds smoke, smog, and most allergy triggers, and is the right target for most homes. Above 13 in a standard 1″ slot, the filter is so dense it can choke a residential blower — if you want hospital-grade filtration, do it with a deep 4–5″ media cabinet, not a denser 1″ filter.',
            },
            {
                h: 'The habits',
                list: [
                    '1″ filters: check monthly in heavy season, change every 1–3 months.',
                    '4–5″ media filters: change every 6–12 months.',
                    'Pets, allergies, or nearby construction: halve those intervals.',
                    'The frame arrow points toward the unit (direction of airflow).',
                    'Write the date on the frame edge — future you will thank you.',
                ],
            },
        ],
    },
]
